import { ChannelHeader } from './ChannelHeader';
import {
  ALL_HEADER_SIZE_BYTE,
  BDF_ID_FULL_TEXT,
  CHANNEL_COUNT_BYTE,
  CHANNEL_HEADER_FIELDS,
  CHANNEL_LABEL_NAME,
  DIGITAL_MAX_NAME,
  DIGITAL_MIN_NAME,
  PHYSICAL_DIMENSION_NAME,
  PHYSICAL_MAX_NAME,
  PHYSICAL_MIN_NAME,
  PREFILTERING_NAME,
  RECORD_COUNT_BYTE,
  RECORD_INTERVAL_SECOND_BYTE,
  RECORDING_ID_BYTE,
  RESERVED_NAME,
  SAMPLE_COUNT_NAME,
  START_DATE_BYTE,
  START_TIME_BYTE,
  SUBJECT_ID_BYTE,
  TRANSDUCER_TYPE_NAME,
  VERSION_FORMAT_BYTE,
} from './const';
import SignalReadableContents from './SignalReadableContents';
import type { FileHeader } from './types';

export default class SignalFileHeader {
  readonly samplingRate: number;
  readonly scaleFactor: number;
  readonly endDate: Date;
  readonly totalDurationMs: number;
  readonly fileHeader: FileHeader;
  readonly channelHeaders: ChannelHeader[];

  constructor(fileHeader: FileHeader, channelHeaders: ChannelHeader[]) {
    const firstChannel = channelHeaders[0];
    this.samplingRate = firstChannel.samples / fileHeader.intervalSec;
    this.scaleFactor = firstChannel.scaleFactor;
    this.totalDurationMs = fileHeader.dataRecordCnt * fileHeader.intervalSec * 1000;
    this.endDate = new Date(fileHeader.startDate.getTime() + this.totalDurationMs);
    this.fileHeader = fileHeader;
    this.channelHeaders = channelHeaders;
  }

  get startDate() {
    return this.fileHeader.startDate;
  }

  isBdf() {
    return this.fileHeader.id === BDF_ID_FULL_TEXT;
  }

  /**
   * BDF 헤더만 파싱하여 메타데이터 추출
   */
  static parseHeader(headerBuffer: ArrayBuffer | SignalReadableContents): SignalFileHeader {
    const contents =
      headerBuffer instanceof SignalReadableContents ? headerBuffer : new SignalReadableContents(headerBuffer);

    // File Header 파싱
    const id = contents.readId();
    const patient = contents.readText(SUBJECT_ID_BYTE);
    const recordId = contents.readText(RECORDING_ID_BYTE);
    const sDate = contents.readText(START_DATE_BYTE);
    const sTime = contents.readText(START_TIME_BYTE);
    const headerLength = contents.readText(ALL_HEADER_SIZE_BYTE);
    const versionFormat = contents.readText(VERSION_FORMAT_BYTE);
    const dataRecords = contents.readText(RECORD_COUNT_BYTE);
    const duration = contents.readText(RECORD_INTERVAL_SECOND_BYTE);
    const channelCnt = Number(contents.readText(CHANNEL_COUNT_BYTE));

    // 날짜 파싱
    const parseStartDateTime = (sDate: string, sTime: string): Date => {
      const date_parts = sDate.split('.');
      const time_parts = sTime.split('.');

      if (date_parts.length !== 3 || time_parts.length !== 3) {
        throw new Error(`Invalid start date/time field (date = ${sDate} / time = ${sTime})`);
      }

      const year = parseInt(date_parts[2]);
      const clipping = year >= 85 && year <= 99 ? 1900 : 2000;

      return new Date(
        year + clipping,
        Number(date_parts[1]) - 1,
        Number(date_parts[0]),
        Number(time_parts[0]),
        Number(time_parts[1]),
        Number(time_parts[2])
      );
    };

    const fileHeader: FileHeader = {
      id,
      subjectId: patient,
      recordId,
      startDate: parseStartDateTime(sDate, sTime),
      headerLength: Number(headerLength),
      versionFormat,
      dataRecordCnt: Number(dataRecords),
      intervalSec: Number(duration),
      channelCnt,
    };

    // Channel Headers 파싱
    const channelHeaders: ChannelHeader[] = Array.from({ length: channelCnt }, () => new ChannelHeader());

    for (let fieldIdx = 0; fieldIdx < CHANNEL_HEADER_FIELDS.length; fieldIdx++) {
      const field = CHANNEL_HEADER_FIELDS[fieldIdx];

      for (let chIdx = 0; chIdx < channelCnt; chIdx++) {
        const data = contents.readText(field.length);

        if (field.name === CHANNEL_LABEL_NAME) {
          channelHeaders[chIdx].label = data;
        } else if (field.name === TRANSDUCER_TYPE_NAME) {
          channelHeaders[chIdx].transducer = data;
        } else if (field.name === PHYSICAL_DIMENSION_NAME) {
          channelHeaders[chIdx].physicalDimensions = data;
        } else if (field.name === PHYSICAL_MIN_NAME) {
          channelHeaders[chIdx].physicalMin = parseFloat(data);
        } else if (field.name === PHYSICAL_MAX_NAME) {
          channelHeaders[chIdx].physicalMax = parseFloat(data);
        } else if (field.name === DIGITAL_MIN_NAME) {
          channelHeaders[chIdx].digitalMin = parseInt(data);
        } else if (field.name === DIGITAL_MAX_NAME) {
          channelHeaders[chIdx].digitalMax = parseInt(data);
        } else if (field.name === PREFILTERING_NAME) {
          channelHeaders[chIdx].preFiltering = data;
        } else if (field.name === SAMPLE_COUNT_NAME) {
          channelHeaders[chIdx].samples = parseInt(data);
        } else if (field.name === RESERVED_NAME) {
          channelHeaders[chIdx].reserved = data;
        }
      }
    }

    if (channelHeaders.length === 0) {
      throw new Error('No channels found in signal file header');
    }

    return new SignalFileHeader(fileHeader, channelHeaders);
  }
}
